import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import { customersListQuery } from "../../supportFunctions/graphql/CustomerQueries";
import {
  getPagination,
  PaginationQueryObject,
  QuerySort,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import {
  VirtualScroller,
  VirtualScrollerLazyEvent,
} from "primereact/virtualscroller";
import { setAppCustomerId } from "../../../App";
import { BaseUrl } from "../../../env";

import classes from "./CustomerSwitcher.module.scss";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";

interface Customer {
  id: string;
  name: string;
  salesforceId: string;
}

const CustomerSwitcher = () => {
  const { t } = useTranslation();
  const cx = classNames.bind(classes);

  const virtualScrollerRef = useRef<VirtualScroller | null>(null);

  const [search, setSearch] = useState<string>("");

  const [customers, setCustomers] = useState<Customer[]>([]);

  const [pagination, setPagination] = useState<{
    pageNumber: number;
    pageSize: number;
    hasNext: boolean;
  }>({
    pageNumber: 1,
    pageSize: 25,
    hasNext: false,
  });

  const searchCustomers = () => {
    const querySort: QuerySort = {
      sortField: "name",
      sortDirection: SortDirection.ASC,
    };

    const paginationInput: PaginationQueryObject = getPagination(
      pagination.pageNumber,
      pagination.pageSize,
      querySort
    );

    const filter =
      search.length > 0
        ? {
            or: {
              name: search,
              domain: search,
              salesforceId: search,
            },
          }
        : null;

    UsersService.api
      .post("", customersListQuery(filter, paginationInput))
      .then((response) => {
        const hasNext = response.data.data.customers.pageInfo.hasNext;

        setPagination((prev) => ({
          ...prev,
          hasNext,
          pageNumber: prev.pageNumber + 1,
        }));

        const newSuggestions = response.data.data.customers.items.map(
          (customer: Customer) => ({
            name: customer.name,
            id: customer.id,
            salesforceId: customer.salesforceId,
          })
        );

        if (pagination.pageNumber > 1) {
          setCustomers((prev) => [...prev, ...newSuggestions]);
        }

        if (pagination.pageNumber === 1) {
          setCustomers(newSuggestions);
        }
      })
      .catch((error: unknown) => {
        /* eslint-disable-next-line no-console*/
        console.log(error);
      });
  };

  const onLazyLoad = (e: VirtualScrollerLazyEvent) => {
    if (customers.length === e.last && pagination.hasNext) {
      searchCustomers();
    }
  };

  const itemTemplate = (item: any) => {
    const isSelected = item.id === UsersService.customer?.id;

    return (
      <div
        role="button"
        className={cx(classes.itemTemplate, { isSelected })}
        onClick={() => {
          UsersService.setActiveCustomerCookie &&
            UsersService.setActiveCustomerCookie(item.id);
          setAppCustomerId(item.id);
          window.location.href = BaseUrl();
        }}
        tabIndex={0}
      >
        <span className={classes.itemName}>{item.name}</span>
        <span className={classes.itemSalesforceId}>{item.salesforceId}</span>
      </div>
    );
  };

  useEffect(() => {
    searchCustomers();
    //Changing the dependency array would require a different programmatic structure, TODO:Investigate useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <>
      <div className={classes.currentCustomer}>
        <i className={cx("fa-solid fa-circle-check", classes.checkIcon)} />
        <p className={cx(classes.subTitle)}>
          <span className={cx(classes.customerName)}>
            {UsersService.customer?.name}
          </span>
          <span className={cx(classes.salesforceId)}>
            {UsersService.customer?.salesforceId}
          </span>
        </p>
      </div>
      <div className={cx(classes.searchContainer)}>
        <span className="p-input-icon-right">
          <i className="pi pi-search text-primary" />
          <InputText
            placeholder={`${t("universal_Search")}...`}
            className={cx(classes.searchInput)}
            value={search}
            onChange={(e) => {
              /*
              The next line of code is necessary to avoid a bug which presents 
              in the PrimeReact component when the user scrolls down too many 
              rows in the virtual scroller and a search is performed.
              Bug: When scrolling down several rows and performing a new search 
                   (in this case, through an Input), the virtual scroller will 
                   show a blank screen.
              Fix: Add a reference to the trigger (our Input) and make it scroll 
                   to top on every event. Notice that left and behavior properties 
                   are required.
              */
              virtualScrollerRef.current?.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
              });
              setSearch(e.target.value);
              setPagination((prev) => ({
                ...prev,
                pageNumber: 1,
              }));
            }}
          />
        </span>
      </div>
      <ul className={cx(classes.virtualScrollerContainer)}>
        {customers.length > 0 ? (
          <VirtualScroller
            ref={virtualScrollerRef}
            className={cx(classes.virtualscroller)}
            items={customers}
            itemTemplate={itemTemplate}
            itemSize={20}
            lazy
            showLoader
            onLazyLoad={onLazyLoad}
          />
        ) : (
          <p>{t("message_no_customers_found")}</p>
        )}
      </ul>
    </>
  );
};

export default CustomerSwitcher;
